import type { WaterQualityData } from "@/lib/conditions/water-quality-status";
import { statusColor, statusLabel } from "@/lib/conditions/water-quality-status";

export function WaterQualityAlert({
  data,
}: {
  data: WaterQualityData;
}) {
  if (
    data.overallStatus === "clear" ||
    data.overallStatus === "unknown" ||
    !data.alertMessage
  ) {
    return null;
  }

  const sc = statusColor(data.overallStatus);

  return (
    <div
      className={`rounded-[12px] border p-4 ${sc.bg} ${sc.border} ${sc.text}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-1 shrink-0 w-2.5 h-2.5 rounded-full ${sc.dot} ${
            data.overallStatus === "discharging" ? "animate-pulse" : ""
          }`}
        />
        <div>
          <p className="text-[13px] font-semibold leading-snug">
            {data.alertMessage}
          </p>
          <p className="text-[11px] mt-1 opacity-75">
            Source: {data.dataSource}
          </p>
        </div>
      </div>
    </div>
  );
}

export function WaterQualityPanel({
  data,
}: {
  data: WaterQualityData;
}) {
  if (data.monitors.length === 0) return null;

  const hasRealData = data.monitors.some((m) => m.status !== "unknown");

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
          Water quality
        </h3>
        <span className="text-[11px] text-text-muted/70">
          {data.monitors.length} nearby sample point
          {data.monitors.length !== 1 ? "s" : ""}
        </span>
      </div>

      {!hasRealData && (
        <div className="text-[12px] text-text-muted bg-warm-bg rounded-[8px] p-3 mb-4">
          Live discharge monitoring is not connected here yet. This panel uses
          nearby official sampling points where available.
        </div>
      )}

      <div className="space-y-2">
        {data.monitors.map((monitor) => {
          const sc = statusColor(monitor.status);
          return (
            <div
              key={monitor.id}
              className={`flex items-center justify-between rounded-[8px] border p-3 ${sc.bg} ${sc.border}`}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className={`w-2 h-2 rounded-full ${sc.dot} ${
                    monitor.status === "discharging" ? "animate-pulse" : ""
                  }`}
                />
                <span className="text-[12px] font-semibold text-navy">
                  {monitor.name}
                </span>
                {monitor.waterCompany && (
                  <span className="text-[11px] text-text-muted">
                    {monitor.waterCompany}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {monitor.distanceKm !== null && (
                  <span className="text-[10px] text-text-muted tabular-nums">
                    {monitor.distanceKm.toFixed(1)} km
                  </span>
                )}
                <span className={`text-[11px] font-semibold ${sc.text}`}>
                  {statusLabel(monitor.status)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {data.recentEvents.length > 0 && (
        <div className="mt-4">
          <h4 className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted mb-2">
            Last 7 days
          </h4>
          <div className="flex gap-[2px] h-[40px] items-end">
            {data.recentEvents.map((event, i) => {
              const maxHours = Math.max(
                ...data.recentEvents.map((e) => e.durationHours),
                1
              );
              const pct = (event.durationHours / maxHours) * 100;
              return (
                <div
                  key={i}
                  className="flex-1 min-w-0 rounded-t-[2px] bg-red-300 transition-all"
                  style={{ height: `${Math.max(pct, 6)}%` }}
                  title={`${event.date}: ${event.durationHours.toFixed(1)}h discharge`}
                />
              );
            })}
          </div>
          {data.recentEvents.length >= 2 && (
            <div className="flex justify-between text-[9px] text-text-muted mt-1">
              <span>{data.recentEvents[0].date}</span>
              <span>
                {data.recentEvents[data.recentEvents.length - 1].date}
              </span>
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-text-muted/60 mt-3">
        Updated{" "}
        {new Date(data.lastChecked).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </p>
    </div>
  );
}
